import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import ManualLayout from '../components/ManualLayout';

interface ContactPageProps {
  language: 'ja' | 'en';
  onLanguageChange: (lang: 'ja' | 'en') => void;
}

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const t = {
  ja: {
    title: 'お問い合わせ',
    description: 'ご質問・ご要望・不具合報告など、お気軽にお問い合わせください。',
    email: 'メールアドレス',
    emailPlaceholder: 'your@email.com',
    subject: '件名',
    subjectPlaceholder: '件名を入力してください',
    body: '本文',
    bodyPlaceholder: 'お問い合わせ内容を入力してください',
    send: '送信',
    sending: '送信中...',
    successTitle: '送信完了',
    successMessage: 'お問い合わせを受け付けました。内容を確認の上、ご返信いたします。',
    errorMessage: '送信に失敗しました。お手数ですが、メールにて直接お問い合わせください。',
    sendAnother: '別のお問い合わせを送信',
    backToManual: 'マニュアルTOPへ戻る',
    required: '必須',
  },
  en: {
    title: 'Contact Us',
    description: 'Feel free to reach out with questions, feature requests, or bug reports.',
    email: 'Email Address',
    emailPlaceholder: 'your@email.com',
    subject: 'Subject',
    subjectPlaceholder: 'Enter subject',
    body: 'Message',
    bodyPlaceholder: 'Enter your message',
    send: 'Send',
    sending: 'Sending...',
    successTitle: 'Sent Successfully',
    successMessage: 'Your inquiry has been received. We will review it and get back to you.',
    errorMessage: 'Failed to send. Please contact us directly via email.',
    sendAnother: 'Send Another Inquiry',
    backToManual: 'Back to Manual',
    required: 'Required',
  },
} as const;

export default function ContactPage({ language, onLanguageChange }: ContactPageProps) {
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const labels = t[language];

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const { error } = await supabase
        .from('contact_submissions')
        .insert({ name: '', email, subject, message: body, status: 'new' });

      if (error) throw error;
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [email, subject, body, language]);

  const handleReset = useCallback(() => {
    setEmail('');
    setSubject('');
    setBody('');
    setStatus('idle');
  }, []);

  if (status === 'success') {
    return (
      <ManualLayout currentPath="/manual/article/support/contact" language={language} onLanguageChange={onLanguageChange}>
        <div className="manual-article">
          <div className="manual-contact-result">
            <div className="manual-contact-result-icon">✓</div>
            <h2 className="manual-contact-result-title">{labels.successTitle}</h2>
            <p className="manual-contact-result-message">{labels.successMessage}</p>
            <div className="manual-contact-result-actions">
              <button className="manual-contact-btn" onClick={handleReset}>{labels.sendAnother}</button>
              <button className="manual-contact-btn-secondary" onClick={() => window.location.hash = '#/manual'}>
                {labels.backToManual}
              </button>
            </div>
          </div>
        </div>
      </ManualLayout>
    );
  }

  return (
    <ManualLayout currentPath="/manual/article/support/contact" language={language} onLanguageChange={onLanguageChange}>
      <div className="manual-article">
        <div className="manual-article-header">
          <h1 className="manual-article-title">{labels.title}</h1>
        </div>

        <p className="manual-contact-description">{labels.description}</p>

        <form className="manual-contact-form" onSubmit={handleSubmit}>
          <div className="manual-contact-field">
            <label className="manual-contact-label">
              {labels.email}
              <span className="manual-contact-required">{labels.required}</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={labels.emailPlaceholder}
              className="manual-contact-input"
            />
          </div>

          <div className="manual-contact-field">
            <label className="manual-contact-label">
              {labels.subject}
              <span className="manual-contact-required">{labels.required}</span>
            </label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={labels.subjectPlaceholder}
              className="manual-contact-input"
            />
          </div>

          <div className="manual-contact-field">
            <label className="manual-contact-label">
              {labels.body}
              <span className="manual-contact-required">{labels.required}</span>
            </label>
            <textarea
              required
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={labels.bodyPlaceholder}
              className="manual-contact-textarea"
            />
          </div>

          {status === 'error' && (
            <div className="manual-contact-error">
              <p>{labels.errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="manual-contact-btn"
          >
            {status === 'sending' ? labels.sending : labels.send}
          </button>
        </form>
      </div>
    </ManualLayout>
  );
}
